<template>
  <div class="v-use-mask-container" :style="containerStyle" ref="containerRef">
    <div class="v-use-mask-content" :class="[shapeClass]" :style="[maskStyle, CssStyleHelpers.filterShadow(props.shadow)]">
      <div class="v-use-mask-image-wrapper">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, onUnmounted, provide } from "vue";
import { getMaskStyles } from "./mediaMaskHelper";
import { CssStyleHelpers } from "../../utils/cssStyleHelpers";
import { useContainerResize } from "./useContainerResize";

const props = defineProps({
  // Включення/вимкнення маски
  enabled: {
    type: Boolean,
    default: true,
  },
  // PNG маска: 'basic-mask-10', 'brush-stroke-1', 'number-1', 'cyrillic-a', и т.д.
  shape: {
    type: String,
    default: "basic-mask-10",
  },
  // Радиус скругления (используется только если нет PNG маски)
  borderRadius: {
    type: [Number, String],
    default: 0,
  },
  // Размер контейнера (застарілі - використовуйте containerStyle)
  width: {
    type: [Number, String],
    default: undefined,
  },
  height: {
    type: [Number, String],
    default: undefined,
  },
  // Стилі контейнера, які передаються ззовні
  containerStyle: {
    type: Object as () => Record<string, string | number> | undefined,
    default: undefined,
  },
  // Позиціонування маски
  positionX: {
    type: Number,
    default: 0,
  },
  positionY: {
    type: Number,
    default: 0,
  },
  // Фон маски
  backgroundColor: {
    type: String,
    default: null,
  },
  // Тінь маски
  shadow: {
    type: Object,
    default: null,
  },
  // Aspect ratio маски для правильного відображення пропорцій
  maskAspectRatio: {
    type: Number,
    default: 1,
  },
  // Режим отображения: contain или cover
  objectFit: {
    type: String as () => "cover" | "contain" | undefined,
    default: undefined,
  },
  // Обріз тіла маски зверху/знизу (%)
  cropTop: { type: Number, default: 0 },
  cropBottom: { type: Number, default: 0 },
});

// Используем хелпер для масок

// Класс формы для CSS маски
const shapeClass = computed(() => {
  return props.shape;
});

// Стили для маски (з підтримкою PNG-масок і прев'ю обрізу через clip-path)
const maskStyle = computed(() => {
  const styles: Record<string, string | undefined> = {
    width: "100%",
    objectFit: "cover",
    objectPosition: "center",
    borderRadius:
      typeof props.borderRadius === "number"
        ? `${props.borderRadius}px`
        : props.borderRadius,
    backgroundColor: props.backgroundColor || undefined,
  };
  if (props.shape && props.enabled) {
    Object.assign(styles, getMaskStyles(props.shape));
  }

  // Для прев'ю в конструкторі обріз робимо через clip-path,
  // щоб форма маски залишалась правильною
  const top = Math.min(100, Math.max(0, props.cropTop));
  const bottom = Math.min(100, Math.max(0, props.cropBottom));
  if (top > 0 || bottom > 0) {
    const value = `inset(${top}% 0 ${bottom}% 0)`;
    styles.clipPath = value;
    styles.WebkitClipPath = value;
  }

  return styles;
});

// Ref для контейнера
const containerRef = ref<HTMLElement | null>(null);

// Используем useContainerResize для предоставления масштаба дочерним компонентам
// (для масштабирования координат изображения)
const { scale } = useContainerResize(containerRef, {
  onBaseSizeInitialized: (size) => {
    // Базовый размер установлен
  },
  onScaleUpdated: (newScale, containerSize) => {
    // Масштаб обновлен при изменении размера контейнера
  },
});


// Примечание: Позиционирование и масштабирование контента применяется только в слое контента (MediaTransformLayer),
// а не в самой маске. Маска остается статичной и отвечает только за форму и стили.

// Стили для контейнера - використовуємо передані ззовні (вже обчислені в useMediaCard)
const containerStyle = computed(() => props.containerStyle || {
  width: props.width || "100%",
  height: props.height || "100%",
});

onMounted(() => {
  // Компонент инициализирован
});
</script>

<style></style>

<style scoped>
.v-use-mask-container {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Размеры устанавливаются через containerStyle (width/height props или aspect-ratio) */
}

.v-use-mask-content {
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.v-use-mask-image-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  min-width: 0;
  min-height: 0;
  justify-content: center;
  align-items: center;
  display: flex;
}

/* Убираем жесткие стили для дочерних элементов, так как они управляются через MediaContentLayer и MediaTransformLayer */
</style>
